import React, { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useArtistAuth } from '../../../contexts/ArtistAuthContext';
import { getResponseStatusBadges, type ResponseStatusBadge } from '../../../api/workAvailability';
import { AccountAvatarBadgeContext } from '../chat/avatars/AccountAvatarTrigger';
import '../../../css/common/ResponseStatus.css';

type Store = { register: (id: number) => () => void; badges: Record<number, ResponseStatusBadge> };
const Context = createContext<Store>({ register: () => () => {}, badges: {} });
export const RESPONSE_STATUS_LABELS: Record<string, string> = {
  normal: '', auto: '勤務予定に合わせる', available: '対応可能です', unavailable: '本日対応不可',
  slow_response: '返信に時間がかかります', off_duty: '業務時間外です',
};
export const RESPONSE_STATUS_ICONS: Record<string, string> = { unavailable: 'pause', off_duty: 'moon', slow_response: 'clock', available: 'check' };
export const RESPONSE_STATUS_HOVER_DELAY_MS = 800;

export function ResponseStatusProvider({ children }: React.PropsWithChildren) {
  const { user } = useAuth();
  const { artistUser } = useArtistAuth();
  const adminId = user?.userId || user?.id;
  const artistId = artistUser?.userId;
  const identity = adminId ? `admin:${adminId}` : artistId ? `artist:${artistId}` : '';
  return <StatusStore key={identity} enabled={Boolean(identity)}>{children}</StatusStore>;
}

function StatusStore({ enabled, children }: React.PropsWithChildren<{ enabled: boolean }>) {
  const [badges, setBadges] = useState<Record<number, ResponseStatusBadge>>({});
  const members = useRef(new Map<number, number>());
  const requestRefresh = useRef<() => void>(() => {});
  const register = useCallback((id: number) => {
    members.current.set(id, (members.current.get(id) || 0) + 1);
    requestRefresh.current();
    return () => {
      const count = (members.current.get(id) || 1) - 1;
      if (count) members.current.set(id, count); else members.current.delete(id);
    };
  }, []);
  useEffect(() => {
    if (!enabled) return;
    let alive = true, running = false, failures = 0, pending = false, generation = 0;
    let timer: ReturnType<typeof setTimeout>;
    const cache = new Map<number, { value: ResponseStatusBadge; at: number }>();
    const schedule = (delay: number) => { clearTimeout(timer); timer = setTimeout(refresh, delay); };
    const refresh = async () => {
      if (!alive || running || document.hidden) return;
      running = true;
      pending = false;
      const requestedGeneration = generation;
      try {
        const now = Date.now();
        const ids = [...members.current.keys()].filter((id) => now - (cache.get(id)?.at || 0) >= 55_000);
        for (let i = 0; i < ids.length && alive; i += 50) {
          const batch = ids.slice(i, i + 50);
          const items = await getResponseStatusBadges(batch);
          if (!alive || requestedGeneration !== generation) return;
          batch.forEach(id => cache.set(id, { value: { adminUserId: id, status: 'normal', note: '', expiresAt: null }, at: Date.now() }));
          items.forEach(value => cache.set(value.adminUserId, { value, at: Date.now() }));
        }
        for (const id of cache.keys()) if (!members.current.has(id)) cache.delete(id);
        if (alive) setBadges(Object.fromEntries([...cache].map(([id, item]) => [id, item.value])));
        failures = 0;
      } catch { failures += 1; /* Background failures must not interrupt chat. */ }
      finally {
        running = false;
        if (alive) schedule(failures ? Math.min(300_000, 15_000 * 2 ** failures) + Math.random() * 3000 : pending ? 80 : 60_000);
      }
    };
    requestRefresh.current = () => { if (running) pending = true; else if (!failures) schedule(80); };
    const invalidate = () => { generation += 1; cache.clear(); failures = 0; pending = true; if (!running) schedule(80); };
    const visibility = () => { if (document.hidden) clearTimeout(timer); else invalidate(); };
    window.addEventListener('casticart:work-availability-updated', invalidate);
    window.addEventListener('casticart:response-status-updated', invalidate);
    document.addEventListener('visibilitychange', visibility);
    schedule(80);
    return () => { alive = false; clearTimeout(timer); requestRefresh.current = () => {}; window.removeEventListener('casticart:work-availability-updated', invalidate); window.removeEventListener('casticart:response-status-updated', invalidate); document.removeEventListener('visibilitychange', visibility); };
  }, [enabled]);
  return <Context.Provider value={{ register, badges }}>{children}</Context.Provider>;
}

export function responseStatusAdminId(message: Record<string, any> = {}): number | undefined {
  if (message.sender_type === 'artist' || message.senderType === 'artist' || message.isAdminMsg === false) return undefined;
  const id = Number(message.admin_user_id || message.adminUserId || message.sender_admin_id
    || ((message.sender_type === 'admin' || message.senderType === 'admin') ? message.sender_id || message.senderId : 0));
  return Number.isSafeInteger(id) && id > 0 ? id : undefined;
}

function useResponseStatusBadge(adminUserId?: number | string | null) {
  const { register, badges } = useContext(Context);
  const id = Number(adminUserId);
  useEffect(() => Number.isSafeInteger(id) && id > 0 ? register(id) : undefined, [id, register]);
  const badge = badges[id];
  const [, refreshExpiry] = useState(0);
  useEffect(() => {
    if (!badge?.expiresAt) return;
    const delay = Date.parse(badge.expiresAt) - Date.now();
    if (delay <= 0) return;
    const timer = setTimeout(() => refreshExpiry(n => n + 1), Math.min(delay + 1, 2_147_483_647));
    return () => clearTimeout(timer);
  }, [badge?.expiresAt]);
  if (!badge || !RESPONSE_STATUS_ICONS[badge.status] || (badge.expiresAt && Date.parse(badge.expiresAt) <= Date.now())) return undefined;
  return badge;
}

export function useResponseStatusSnapshots(adminUserIds: Array<number | string | null | undefined>) {
  const { register, badges } = useContext(Context);
  const key = [...new Set(adminUserIds.map(Number).filter(id => Number.isSafeInteger(id) && id > 0))]
    .sort((a, b) => a - b)
    .join(',');
  useEffect(() => {
    if (!key) return undefined;
    const unregister = key.split(',').map(Number).map(register);
    return () => unregister.forEach(remove => remove());
  }, [key, register]);
  return badges;
}

/** Only the viewer-filtered badge projection belongs in another person's profile. */
export function ResponseStatusSummary({ adminUserId }: { adminUserId?: number | string | null }) {
  const badge = useResponseStatusBadge(adminUserId);
  if (!badge) return null;
  return <div className="response-status-summary" role="status" aria-label="現在の対応状況">
    <div className="response-status-summary__label">
      <span className="response-status-symbol" data-response-status={badge.status} aria-hidden="true"><i className={`fa-solid fa-${RESPONSE_STATUS_ICONS[badge.status]}`} /></span>
      <span>{RESPONSE_STATUS_LABELS[badge.status]}</span>
    </div>
    {badge.note ? <div className="response-status-summary__note">{badge.note}</div> : null}
  </div>;
}

export function ResponseStatusAvatar({ adminUserId, children, badgePlacement = 'overlay' }: React.PropsWithChildren<{ adminUserId?: number | string | null; badgePlacement?: 'overlay' | 'avatar' }>) {
  const badge = useResponseStatusBadge(adminUserId);
  const tooltipId = useId();
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [interacting, setInteracting] = useState(false);
  const [tooltip, setTooltip] = useState<{ left: number; top: number; above: boolean } | null>(null);
  const closeTooltip = useCallback(() => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = undefined;
    setInteracting(false);
    setTooltip(null);
  }, []);
  const showTooltip = (target: HTMLSpanElement) => {
    const rect = target.getBoundingClientRect();
    const above = rect.top > 64;
    setTooltip({ left: Math.max(8, Math.min(rect.left, window.innerWidth - 236)), top: above ? rect.top - 8 : rect.bottom + 8, above });
  };
  const scheduleTooltip = (event: React.MouseEvent<HTMLSpanElement>) => {
    closeTooltip();
    setInteracting(true);
    const target = event.currentTarget;
    hoverTimer.current = setTimeout(() => showTooltip(target), RESPONSE_STATUS_HOVER_DELAY_MS);
  };
  useEffect(() => {
    if (!interacting) return;
    window.addEventListener('scroll', closeTooltip, true); window.addEventListener('resize', closeTooltip);
    return () => { clearTimeout(hoverTimer.current); window.removeEventListener('scroll', closeTooltip, true); window.removeEventListener('resize', closeTooltip); };
  }, [interacting, closeTooltip]);
  const icon = badge ? RESPONSE_STATUS_ICONS[badge.status] : '';
  useEffect(() => { closeTooltip(); }, [adminUserId, badge?.status, badge?.note, closeTooltip]);
  if (!children || !badge || !icon) return <>{children}</>;
  const label = [RESPONSE_STATUS_LABELS[badge.status], badge.note].filter(Boolean).join(' — ');
  const statusIcon = <span className="response-status-symbol" data-response-status={badge.status} role="img" aria-label={label}><i className={`fa-solid fa-${icon}`} aria-hidden="true" /></span>;
  return <span className="response-status-avatar" aria-describedby={tooltip ? tooltipId : undefined} onMouseEnter={scheduleTooltip} onMouseLeave={closeTooltip} onFocusCapture={event => { closeTooltip(); setInteracting(true); showTooltip(event.currentTarget); }} onBlurCapture={closeTooltip}>
    {badgePlacement === 'avatar'
      ? <AccountAvatarBadgeContext.Provider value={statusIcon}>{children}</AccountAvatarBadgeContext.Provider>
      : <>{children}<span className="response-status-avatar__badge">{statusIcon}</span></>}
    {tooltip ? createPortal(<span id={tooltipId} role="tooltip" className="response-status-tooltip" style={{ left: tooltip.left, top: tooltip.top, transform: tooltip.above ? 'translateY(-100%)' : undefined }}>{label}</span>, document.body) : null}
  </span>;
}
